import { HashRouter, Routes, Route, Navigate } from 'react-router-dom'
import { LicenseProvider, useLicense } from './contexts/LicenseContext'
import { GenerationProvider } from './contexts/GenerationContext'
import Layout from './components/Layout'
import Home from './pages/Home'
import ModeSelect from './pages/ModeSelect'
import OutsideIn from './pages/OutsideIn'
import InsideOut from './pages/InsideOut'
import Dashboard from './pages/Dashboard'
import Settings from './pages/Settings'
import Templates from './pages/Templates'
import Activation from './pages/Activation'
import Admin from './pages/Admin'
import type { ReactNode } from 'react'

function RequireLicense({ children }: { children: ReactNode }) {
  const { status, loading } = useLicense()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div
            className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white text-lg font-bold mb-3 animate-pulse"
            style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
          >
            TN
          </div>
          <p className="text-sm text-gray-500">Checking license...</p>
        </div>
      </div>
    )
  }

  if (!status?.isValid) {
    // Full license expired and activated — stay in app for read-only dashboard viewing
    if (status?.isExpired && status?.isActivated && status?.licenseType === 'full') {
      return <>{children}</>
    }
    // Trial license expired — full lockout, redirect to activation
    return <Navigate to="/activate" replace />
  }

  return <>{children}</>
}

export default function App() {
  return (
    <HashRouter>
      <LicenseProvider>
        <GenerationProvider>
          <Routes>
            <Route path="/activate" element={<Activation />} />
            <Route
              path="/"
              element={
                <RequireLicense>
                  <Layout />
                </RequireLicense>
              }
            >
              <Route index element={<Home />} />
              <Route path="new" element={<ModeSelect />} />
              <Route path="outside-in/:projectId?" element={<OutsideIn />} />
              <Route path="inside-out/:projectId?" element={<InsideOut />} />
              <Route path="dashboard/:projectId" element={<Dashboard />} />
              <Route path="settings" element={<Settings />} />
              <Route path="templates" element={<Templates />} />
              <Route path="admin" element={<Admin />} />
            </Route>
          </Routes>
        </GenerationProvider>
      </LicenseProvider>
    </HashRouter>
  )
}
